import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../components/auth/useAuth'
import { useSelector } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'

export default function UserMenu() {
    const { t } = useTranslation()
    const { clientauth, logoutClient } = useAuth()
    const { dashboard, unreadCount, avatar } = useSelector(state => state.users)
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)
    const [activeStudentIdx, setActiveStudentIdx] = useState(0)
    const ref = useRef(null)

    const students = dashboard?.students || []

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const displayName = clientauth?.name || 'Usuario'
    const displayEmail = clientauth?.email || ''
    const displayRole = clientauth?.rol === 'student' ? t('admin.users.student') : clientauth?.rol === 'admin' ? t('admin.users.admin') : ''
    const avatarSrc = avatar || '/images/profile.svg'

    const menuItems = [
        {
            label: t('admin.dashboard.profile'),
            desc: t('admin.dashboard.profiledescription'),
            icon: 'fa-regular fa-user',
            to: `/${clientauth?.rol}/profile`,
            variant: 'primary'
        },
        {
            label: t('admin.dashboard.academic'),
            desc: t('admin.dashboard.academicdescription'),
            icon: null,
            imgSrc: '/images/openbook.svg',
            to: `/${clientauth?.rol}/academic`,
            variant: 'primary'
        },
        {
            label: t('admin.dashboard.administration'),
            desc: t('admin.dashboard.administrationdescription'),
            icon: null,
            imgSrc: '/images/document.svg',
            to: `/${clientauth?.rol}/administration`,
            variant: 'primary'
        },
        {
            label: t('admin.dashboard.comunications'),
            desc: t('admin.dashboard.comunicationsdescription'),
            icon: null,
            imgSrc: '/images/chat.svg',
            to: `/${clientauth?.rol}/communications`,
            variant: 'primary'
        }
    ]

    const bottomItems = [
        {
            label: 'Configuración',
            desc: 'Preferencias y ajustes',
            icon: 'fa-solid fa-gear',
            to: `/${clientauth?.rol}/settings`,
            variant: 'secondary'
        },
        {
            label: 'Ayuda y soporte',
            desc: 'Centro de ayuda',
            icon: 'fa-regular fa-circle-question',
            to: `/${clientauth?.rol}/help`,
            variant: 'secondary'
        }
    ]

    return (
        <div className='user-menu-wrapper' ref={ref}>
            <div className='user-menu-trigger d-flex align-items-center gap-3' onClick={() => setOpen(!open)}>
                <div className='position-relative'>
                    <i className="fa-regular fa-bell fs-5 text-dark"></i>
                    {unreadCount > 0 && (
                        <span className='user-menu-badge'>{unreadCount > 99 ? '99+' : unreadCount}</span>
                    )}
                </div>
                <div className='d-flex align-items-center gap-2'>
                    <img src={avatarSrc} alt="avatar" className='user-menu-avatar' />
                    <div className='d-none d-md-block text-end'>
                        <p className='mb-0 fw-semibold text-dark' style={{ fontSize: '16px', lineHeight: 1.2 }}>{displayName}</p>
                        <p className='mb-0 text-muted' style={{ fontSize: '13px' }}>{displayRole}</p>
                    </div>
                </div>
            </div>

            {open && (
                <div className='user-menu-dropdown'>
                    <div className='user-menu-header'>
                        <div className='d-flex align-items-center gap-3'>
                            <img src={avatarSrc} alt="avatar" className='user-menu-header-avatar' />
                            <div className='text-white'>
                                <p className='mb-0 fw-bold' style={{ fontSize: '24px', lineHeight: 1.2 }}>{displayName}</p>
                                <p className='mb-0' style={{ fontSize: '16px', opacity: 0.9 }}>{displayEmail}</p>
                                {displayRole && (
                                    <span className='user-menu-role-badge'>{displayRole}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {students.length > 1 && (
                        <div className='user-menu-students'>
                            <p className='user-menu-students-title'>{t('users.switchstudent') || 'CAMBIAR DE ALUMNO'}</p>

                            {students.map((student, idx) => (
                                <div
                                    key={student.id || idx}
                                    className={`user-menu-student-item ${idx === activeStudentIdx ? 'active' : ''}`}
                                    onClick={() => setActiveStudentIdx(idx)}
                                >
                                    <div className='d-flex align-items-center justify-content-between w-100'>
                                        <div>
                                            <p className={`mb-0 fw-semibold ${idx === activeStudentIdx ? 'text-white' : 'text-dark'}`}
                                                style={{ fontSize: '15px' }}>
                                                {student.name}
                                            </p>
                                            <p className={`mb-0 ${idx === activeStudentIdx ? 'text-white-50' : 'text-muted'}`}
                                                style={{ fontSize: '13px' }}>
                                                {student.group || student.courseName || ''}
                                            </p>
                                        </div>
                                        {idx === activeStudentIdx && (
                                            <i className="fa-solid fa-circle-check text-white fs-5"></i>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className='user-menu-body'>
                        {menuItems.map((item, i) => (
                            <Link key={i} to={item.to} className='user-menu-link' onClick={() => setOpen(false)}>
                                <div className='d-flex align-items-center gap-3 w-100'>
                                    <div className={`user-menu-icon user-menu-icon-${item.variant}`}>
                                        {item.imgSrc ? <img src={item.imgSrc} alt="" className='user-menu-svg-icon' /> : <i className={item.icon}></i>}
                                    </div>
                                    <div className='flex-grow-1'>
                                        <p className='mb-0 fw-semibold text-dark' style={{ fontSize: '15px' }}>{item.label}</p>
                                        <p className='mb-0 text-muted' style={{ fontSize: '13px' }}>{item.desc}</p>
                                    </div>
                                    {item.badge && (
                                        <span className='user-menu-badge-pill'>{item.badge}</span>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>

                    <div className='user-menu-divider' />

                    <div className='user-menu-body'>
                        {bottomItems.map((item, i) => (
                            <Link key={i} to={item.to} className='user-menu-link' onClick={() => setOpen(false)}>
                                <div className='d-flex align-items-center gap-3 w-100'>
                                    <div className={`user-menu-icon user-menu-icon-${item.variant}`}>
                                        <i className={item.icon}></i>
                                    </div>
                                    <div>
                                        <p className='mb-0 fw-semibold text-dark' style={{ fontSize: '15px' }}>{item.label}</p>
                                        <p className='mb-0 text-muted' style={{ fontSize: '13px' }}>{item.desc}</p>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    <div className='user-menu-divider' />

                    <div onClick={() => { logoutClient(); navigate('/') }} className='user-menu-link user-menu-logout' style={{ cursor: 'pointer' }}>
                        <div className='d-flex align-items-center gap-3 w-100'>
                            <div className='user-menu-icon user-menu-icon-danger'>
                                <i className="fa-solid fa-arrow-right-from-bracket"></i>
                            </div>
                            <div>
                                <p className='mb-0 fw-semibold' style={{ fontSize: '15px', color: '#dc2626' }}>{t('signin.logout')}</p>
                                <p className='mb-0 text-muted' style={{ fontSize: '13px' }}>{t('signin.logoutDescription') || 'Salir del portal'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
